import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import {
  CaseDetailModel,
  ManageStudentDrawerParams,
  SortState,
  User,
} from '../../../insights.interface';
import { InsightMode } from '../../../insights.enums';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import {
  BehaviorSubject,
  combineLatest,
  finalize,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { CaseMemberService } from '../case-member.service';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonService } from '../../../../../shared/services/common.service';
import { LoaderService } from '../../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { SearchInputComponent } from '../../../../../components/search-input/search-input.component';

import { OrganizationSearchComponent } from '../../../../../components/organization-search/organization-search.component';
import { AuditContextService } from '../../../../audit-logs/audit-context.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { userDetailModel } from '../../../../organizations/organization.interface';
import { OrganizationSelectionService } from '../../../../../core/services/organization-selection.service';

interface CaseOrganization {
  id: number;
  organizationName: string;
  parentOrganizationId: number | null;
}

@Component({
  selector: 'app-add-team-member',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzSelectModule,
    NzTableModule,
    NzButtonModule,
    NzInputModule,
    CommonModule,
    NzIconModule,

    ReactiveFormsModule,
    NzSpinModule,
    NzFormModule,
    FormsModule,
    SearchInputComponent,

    OrganizationSearchComponent,
  ],
  templateUrl: './add-team-member.component.html',
})
export class AddTeamMemberComponent implements OnInit, OnDestroy {
  private readonly caseMemberService = inject(CaseMemberService);

  caseOrganizations$!: Observable<CaseOrganization[]>;
  usersBySchool$!: Observable<User[]>;
  loading$: Observable<boolean> = of(false);

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly destroy$ = new Subject<void>();

  existingMemberIds = new Set<number>();

  schoolForm!: FormGroup;
  searchTerm = '';

  listOfColumn = [
    {
      title: 'Name',
      key: 'name',
      sortOrder: null,
      sortFn: true,
    },
    {
      title: 'Organization Name',
      key: 'organizationName',
      sortOrder: null,
      sortFn: true,
    },
    {
      title: '',
      key: 'actions',
      sortFn: false,
    },
  ];

  caseParams: ManageStudentDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.CREATE,
  };

  sortOrder: number | undefined = undefined;
  sortField = '';
  pageSize = 10;
  pageIndex = 1;
  totalRows = 0;
  sortState: SortState = {
    key: '',
    value: null,
  };
  userData: userDetailModel | null = null;
  orgId: number | null = 0;

  constructor(
    private readonly drawerService: DrawerService,
    private readonly fb: FormBuilder,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService,
    private readonly authService: AuthService,
    private readonly orgSelectionService: OrganizationSelectionService,
    private readonly auditContextService: AuditContextService
  ) {
    this.authService.userData$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.userData = res;
    });
    this.initializeForm();
    this.setupObservables();
    this.setupDrawerSubscription();
  }

  ngOnInit(): void {
    this.orgSelectionService.orgId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(orgId => {
        const newOrgId = orgId === null ? 0 : orgId;
        if (newOrgId !== this.orgId) {
          this.orgId = newOrgId;
          this.pageIndex = 1;
          if (this.orgId !== 0) {
            this.loadUsers(this.orgId, this.searchTerm);
          }
        }
      });

    this.loadOrganizations();
  }

  ngOnDestroy(): void {
    this.orgSelectionService.setOrgId(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSchoolChange(orgId: number | null): void {
    if (orgId) {
      this.loadUsers(orgId, this.searchTerm);
    } else {
      this.caseMemberService.clearUsers();
    }
  }

  onCreateMember(data: User): void {
    if (!data) return;

    this.loaderService.show();

    const original = {};
    const newData = {
      caseId: this.caseParams.case?.id ?? 0,
      userId: data.id,
      userName: data.name,
      isSupervisor: false,
    };

    const labelMap: Record<string, string> = {
      caseId: 'Case ID',
      userId: 'User ID',
      userName: 'Name',
      isSupervisor: 'Is Supervisor',
    };

    const sensitiveFields: string[] = [];
    const excludeFields: string[] = ['caseId', 'userId', 'isSupervisor'];

    const _modifiedFields = this.auditContextService.buildFieldChanges(
      original,
      newData,
      { sensitiveFields, labelMap, excludeFields }
    );

    const payload = { ...newData, _modifiedFields };

    this.caseMemberService
      .createCaseMember(payload)
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'Team member added successfully'
          );
          this.drawerService.closeDrawer(DRAWER_IDS.ADD_TEAM_MEMBER);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors?.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify(
            'error',
            errorMessages?.length
              ? errorMessages
              : ['Failed to add team member'],
            message
          );
        },
      });
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, pageIndex, sort } = params;
    this.pageSize = pageSize;
    this.pageIndex = pageIndex;

    const currentSort = sort.find(item => item.value !== null);
    if (currentSort) {
      this.sortState = {
        key: currentSort.key,
        value: currentSort.value,
      };
      this.sortField = currentSort.key;
      this.sortOrder = currentSort.value === 'ascend' ? 1 : 0;
    } else {
      this.resetSort();
    }

    this.loadUsers(this.orgId ?? 0, this.searchTerm);
  }

  private initializeForm(): void {
    this.schoolForm = this.fb.group({
      orgId: [null],
      caseId: [this.caseParams.case?.id || 0],
    });
  }

  private setupObservables(): void {
    this.setupCaseOrganizations();
    this.setupUsersBySchool();
    this.loading$ = this.loaderService.loading$;
  }

  private setupCaseOrganizations(): void {
    this.caseOrganizations$ = combineLatest([
      this.caseMemberService.caseOrganizations$,
      this.loaderService.loading$,
    ]).pipe(map(([response]) => response?.result || []));
  }

  private setupUsersBySchool(): void {
    this.usersBySchool$ = combineLatest([
      this.caseMemberService.users$,
      this.loaderService.loading$,
    ]).pipe(
      map(([response]) => {
        if (response?.result) {
          this.totalRows = response.result.totalRows;
        }
        return response?.result?.items || [];
      })
    );
  }

  handleSearch(searchTerm: string): void {
    this.loadUsers(this.orgId ?? 0, searchTerm);
  }

  private setupDrawerSubscription(): void {
    this.loaderService.show();
    this.drawerService
      .getParams<ManageStudentDrawerParams>(DRAWER_IDS.ADD_TEAM_MEMBER)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: params => {
          if (params) {
            this.caseParams = params;
            this.existingMemberIds = new Set(
              params.memberList?.map(member => member.caseMemberId) ?? []
            );
          }
        },
        error: () => {
          this.commonService.notify(
            'error',
            'Failed to load drawer parameters'
          );
          this.loaderService.hide();
        },
      });
  }

  private resetSort(): void {
    this.sortField = '';
    this.sortOrder = undefined;
    this.sortState = {
      key: '',
      value: null,
    };
  }

  orgChange(value: number | null) {
    this.orgId = value;
    this.orgSelectionService.setOrgId(value);
    this.pageIndex = 1;
    this.loadUsers(this.orgId ?? 0, this.searchTerm);
  }

  private loadUsers(orgId: number, searchTerm = ''): void {
    this.loaderService.show();

    const params: {
      organizationId?: number;
      pageSize: number;
      page: number;
      searchTerm: string;
      Sort: string;
      sortOrder: number;
    } = {
      pageSize: this.pageSize,
      page: this.pageIndex,
      searchTerm: searchTerm.trim(),
      Sort: this.sortField,
      sortOrder: this.sortOrder ?? 0,
    };

    if (orgId !== 0) {
      params.organizationId = orgId;
    }

    this.caseMemberService
      .getAllUsers(params)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors?.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify(
            'error',
            errorMessages?.length ? errorMessages : ['Failed to fetch users'],
            message
          );
          this.loaderService.hide();
        },
      });
  }

  private loadOrganizations(): void {
    this.loaderService.show();
    this.caseMemberService
      .getCaseOrganizations(this.caseParams.case?.id ?? 0)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
          this.loadingSubject.next(false);
        })
      )
      .subscribe({
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors?.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify(
            'error',
            errorMessages?.length
              ? errorMessages
              : ['Failed to load organizations'],
            message
          );
          this.loaderService.hide();
        },
      });
  }
}
